import AccountDeletion from "../components/AccountDeletion";
import { setSessionToken } from "../lib/sessionToken";
import AdminMfaSection from "../components/AdminMfaSection";
import PublicProfileSection from "../components/PublicProfileSection";
import SubscriptionRecovery from "../components/SubscriptionRecovery";
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { Header } from "../components/Header";
import SubscriptionNotice from "../components/SubscriptionNotice";
import StorePurchaseHelp from "../components/StorePurchaseHelp";
import { useAuth } from "../context/AuthContext";
import { LANGUAGES } from "../i18n";
import { dateLocale } from "../lib/datetime";
import { readFileAsDataURL, getCroppedBlob } from "../lib/cropImage";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import { User, Lock, CreditCard, Trash, SignOut, Warning, CaretRight, Camera } from "@phosphor-icons/react";
import { isNative } from "../lib/native";
import { manageNativeSubscriptions } from "../lib/iap";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Section = ({ icon: Icon, title, children, testid }) => (
  <div className="hm-card p-5 mt-4" data-testid={testid}>
    <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-white/60 mb-4">
      <Icon size={17} weight="bold" color="#D4AF37" /> {title}
    </p>
    {children}
  </div>
);

const Field = ({ label, ...props }) => (
  <label className="block mb-3">
    <span className="block text-[11px] font-semibold uppercase tracking-wider text-white/40 mb-1.5">{label}</span>
    <input
      {...props}
      className="w-full h-11 px-3.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-gold/60 transition-colors"
    />
  </label>
);

export default function AccountSettings() {
  const { t, i18n } = useTranslation();
  const { logout, refreshUser, user, subscription: sub } = useAuth();
  const navigate = useNavigate();

  const [p, setP] = useState(null);
  // profile info
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileRef = useRef(null);
  // Recadrage
  const [cropSrc, setCropSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedPixels, setCroppedPixels] = useState(null);
  const [lang, setLang] = useState(i18n.language?.slice(0, 2) || "fr");
  const [savingInfo, setSavingInfo] = useState(false);
  // password
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confPwd, setConfPwd] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);
  // subscription
  const [subBusy, setSubBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  // delete
  const [showDelete, setShowDelete] = useState(false);
  const [showRetention, setShowRetention] = useState(false);

  const isOAuth = !!p?.auth_provider;
  const isPaid = user?.plan && user.plan !== "free";
  const selectedSubscription = sub?.subscriptions?.find((s) => s.provider === sub.provider);
  const accessUntil = selectedSubscription?.grace_expires_at || selectedSubscription?.expires_at;

  useEffect(() => {
    api.get("/profile").then((r) => {
      setP(r.data);
      setName(r.data.name || "");
      setEmail(r.data.email || "");
      setAvatarUrl(r.data.avatar_url || null);
      if (r.data.preferred_lang) setLang(r.data.preferred_lang);
    });
  }, []);

  const saveInfo = async (e) => {
    e.preventDefault();
    setSavingInfo(true);
    const emailChanged = email.trim().toLowerCase() !== (p?.email || "").toLowerCase();
    // Changer d'adresse exige le mot de passe actuel (audit sécurité C05).
    let currentPassword;
    if (emailChanged && p?.has_password) {
      currentPassword = window.prompt(t("account.email_change_password"));
      if (!currentPassword) { setSavingInfo(false); return; }
    }
    try {
      const { data } = await api.patch("/profile", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        preferred_lang: lang,
        origin_url: window.location.origin,
        ...(currentPassword ? { current_password: currentPassword } : {}),
      });
      setP((prev) => ({ ...prev, ...data }));
      if (lang !== i18n.language?.slice(0, 2)) {
        i18n.changeLanguage(lang);
        try { localStorage.setItem("hitmax_lang", lang); } catch {}
      }
      await refreshUser();
      if (emailChanged) setEmail(p?.email || "");  // l'ancienne adresse reste active jusqu'à confirmation
      toast.success(emailChanged ? t("account.email_change_pending", { email: email.trim().toLowerCase() }) : t("account.saved"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("account.error"));
    } finally {
      setSavingInfo(false);
    }
  };

  const onPickPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const url = await readFileAsDataURL(f);
    setCropSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    if (fileRef.current) fileRef.current.value = "";
  };

  const uploadAvatarBlob = async (blob) => {
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], "avatar.jpg", { type: "image/jpeg" }));
      const { data } = await api.post("/account/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAvatarUrl(data.avatar_url);
      await refreshUser();
      toast.success(t("account.photo_updated"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("account.photo_error"));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const confirmCrop = async () => {
    if (!croppedPixels || !cropSrc) return;
    const src = cropSrc;
    setCropSrc(null);
    try {
      const blob = await getCroppedBlob(src, croppedPixels, 400);
      await uploadAvatarBlob(blob);
    } catch {
      toast.error(t("account.photo_error"));
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPwd.length < 6) return toast.error(t("account.password_too_short"));
    if (newPwd !== confPwd) return toast.error(t("account.password_mismatch"));
    setSavingPwd(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: curPwd, new_password: newPwd });
      // Les autres sessions sont fermées ; cet appareil reçoit un nouveau jeton (audit sécurité C06).
      if (isNative() && data?.token) {
        setSessionToken(data.token); // C10
      }
      setCurPwd(""); setNewPwd(""); setConfPwd("");
      toast.success(t("account.password_changed"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("account.error"));
    } finally {
      setSavingPwd(false);
    }
  };

  const cancelSub = async () => {
    if (isNative()) {
      try { await manageNativeSubscriptions(sub?.provider); }
      catch (err) { toast.error(err?.message || t("account.error")); }
      return;
    }
    navigate("/paywall");
  };

  const doCancel = async () => {
    setShowRetention(false);
    navigate("/paywall");
  };

  const acceptRetention = async () => {
    setSubBusy(true);
    try {
      setShowRetention(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("account.error"));
    } finally { setSubBusy(false); }
  };

  const resumeSub = async () => {
    if (isNative()) {
      try { await manageNativeSubscriptions(sub?.provider); }
      catch (err) { toast.error(err?.message || t("account.error")); }
      return;
    }
    navigate("/paywall");
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      if (isNative()) {
        await manageNativeSubscriptions(sub?.provider);
        setPortalBusy(false);
        return;
      }
      navigate("/paywall");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("account.error"));
      setPortalBusy(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString(dateLocale(), { day: "2-digit", month: "long", year: "numeric" }); }
    catch { return iso; }
  };

  if (!p) return (<><Header showSearch={false} /><div className="px-5 pt-6 space-y-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-32 rounded-2xl skeleton" />)}</div></>);

  return (
    <>
      <Header showSearch={false} />
      <div className="px-5 pt-5 pb-10" data-testid="account-page">
        <h1 className="font-display text-2xl font-bold tracking-tight">{t("account.title")}</h1>
        <p className="text-sm text-white/40 mt-1">{t("account.subtitle")}</p>

        {/* ---------- Informations ---------- */}
        <Section icon={User} title={t("account.profile_section")} testid="account-info-section">
          <form onSubmit={saveInfo}>
            <div className="flex items-center gap-4 mb-4">
              <div className="relative">
                <div className="w-20 h-20 rounded-2xl overflow-hidden flex items-center justify-center bg-gold/15 border border-gold/40" data-testid="account-avatar-preview">
                  {avatarUrl ? (
                    <img src={`${BACKEND_URL}${avatarUrl}`} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="font-numeral font-black text-3xl text-gold">{(name?.[0] || p.name?.[0] || "?").toUpperCase()}</span>
                  )}
                </div>
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploadingPhoto}
                  data-testid="account-avatar-btn" aria-label={t("account.change_photo")}
                  className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-gold text-black flex items-center justify-center border-2 border-[#141414] disabled:opacity-60">
                  <Camera size={15} weight="fill" />
                </button>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                  data-testid="account-avatar-input" onChange={onPickPhoto} />
              </div>
              <div>
                <p className="text-sm font-bold">{t("account.photo")}</p>
                <p className="text-[11px] text-white/40">{uploadingPhoto ? t("account.uploading") : t("account.photo_hint")}</p>
              </div>
            </div>
            <Field label={t("account.name")} value={name} onChange={(e) => setName(e.target.value)} data-testid="account-name-input" />
            <Field label={t("account.email")} type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="account-email-input" />
            <span className="block text-[11px] font-semibold uppercase tracking-wider text-white/40 mb-1.5">{t("account.lang")}</span>
            <div className="flex gap-2 mb-4">
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  data-testid={`account-lang-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`flex-1 h-11 rounded-xl border text-sm font-bold flex items-center justify-center gap-1.5 transition-colors ${lang === l.code ? "border-gold/60 bg-gold/15 text-gold" : "border-white/10 bg-white/5 text-white/60 hover:bg-white/10"}`}
                >
                  <span>{l.flag}</span> {l.short}
                </button>
              ))}
            </div>
            <button type="submit" disabled={savingInfo} data-testid="account-save-info-btn"
              className="w-full h-11 rounded-xl bg-gold text-black font-bold text-sm hover:bg-gold/90 transition-colors disabled:opacity-60">
              {savingInfo ? t("account.saving") : t("account.save")}
            </button>
          </form>
        </Section>

        {/* ---------- Mot de passe ---------- */}
        <Section icon={Lock} title={t("account.password_section")} testid="account-password-section">
          {isOAuth ? (
            <p className="text-sm text-white/50" data-testid="account-oauth-nopwd">{t("account.oauth_no_password")}</p>
          ) : (
            <form onSubmit={changePassword}>
              <Field label={t("account.current_password")} type="password" value={curPwd} onChange={(e) => setCurPwd(e.target.value)} data-testid="account-current-pwd" autoComplete="current-password" />
              <Field label={t("account.new_password")} type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="account-new-pwd" autoComplete="new-password" />
              <Field label={t("account.confirm_password")} type="password" value={confPwd} onChange={(e) => setConfPwd(e.target.value)} data-testid="account-confirm-pwd" autoComplete="new-password" />
              <button type="submit" disabled={savingPwd || !curPwd || !newPwd} data-testid="account-change-pwd-btn"
                className="w-full h-11 rounded-xl bg-white/10 border border-white/15 text-white font-bold text-sm hover:bg-white/15 transition-colors disabled:opacity-50">
                {savingPwd ? t("account.saving") : t("account.change_password")}
              </button>
            </form>
          )}
        </Section>

        <PublicProfileSection />
        <AdminMfaSection />

        {/* ---------- Abonnement ---------- */}
        <Section icon={CreditCard} title={t("account.sub_section")} testid="account-sub-section">
          <SubscriptionNotice />
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-white/50">{t("account.current_plan")}</span>
            <span data-testid="account-plan-badge" className="px-2.5 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-wider bg-gold/18 text-gold">
              {t(`profile.plan_${user?.plan}`)}
            </span>
          </div>

          {!isPaid && (
            <>
              <p className="text-sm text-white/40 mb-3" data-testid="account-no-sub">{t("account.no_sub")}</p>
              <button onClick={() => navigate("/passer-pro")} data-testid="account-upgrade-btn"
                className="w-full h-11 rounded-xl bg-gold text-black font-bold text-sm hover:bg-gold/90 transition-colors flex items-center justify-center gap-2">
                {t("account.upgrade")} <CaretRight size={16} weight="bold" />
              </button>
            </>
          )}

          {isPaid && (
            <div className="space-y-3">
              {sub?.cancel_at_period_end && accessUntil ? (
                <p className="text-[13px] text-amber-300/90" data-testid="account-cancels-on">{t("account.cancels_on", { date: fmtDate(accessUntil) })}</p>
              ) : sub?.renews_at ? (
                <p className="text-[13px] text-white/50" data-testid="account-renews-on">{t("account.renews_on", { date: fmtDate(sub.renews_at) })}</p>
              ) : null}

              {sub?.cancel_at_period_end ? (
                <button onClick={resumeSub} disabled={subBusy} data-testid="account-resume-btn"
                  className="w-full h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 font-bold text-sm hover:bg-emerald-500/25 transition-colors disabled:opacity-60">
                  {subBusy ? t("account.cancelling") : t("account.resume_sub")}
                </button>
              ) : (
                <button onClick={cancelSub} disabled={subBusy} data-testid="account-cancel-btn"
                  className="w-full h-11 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 font-bold text-sm hover:bg-red-500/20 transition-colors disabled:opacity-60">
                  {subBusy ? t("account.cancelling") : t("account.cancel_sub")}
                </button>
              )}

              <button onClick={openPortal} disabled={portalBusy} data-testid="account-portal-btn"
                className="w-full h-11 rounded-xl bg-white/5 border border-white/10 text-white/70 font-semibold text-sm hover:bg-white/10 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                <CreditCard size={16} weight="bold" /> {t("account.manage_billing")}
              </button>
            </div>
          )}
        </Section>

        <StorePurchaseHelp />
      <SubscriptionRecovery />

        {/* ---------- Déconnexion ---------- */}
        <button onClick={logout} data-testid="account-logout-btn"
          className="w-full mt-4 h-12 flex items-center justify-center gap-2 rounded-2xl border border-white/15 bg-transparent text-white/70 font-bold text-sm hover:bg-white/5 hover:text-white transition-colors">
          <SignOut size={18} weight="bold" /> {t("account.logout")}
        </button>

        {/* ---------- Zone sensible ---------- */}
        <Section icon={Warning} title={t("account.danger_section")} testid="account-danger-section">
          <p className="text-sm text-white/40 mb-3">{t("account.delete_warn")}</p>
          <button onClick={() => setShowDelete(true)} data-testid="account-delete-btn"
            className="w-full h-11 rounded-xl bg-red-500/10 border border-red-500/40 text-red-300 font-bold text-sm hover:bg-red-500/20 transition-colors flex items-center justify-center gap-2">
            <Trash size={16} weight="bold" /> {t("account.delete_account")}
          </button>
        </Section>
      </div>

      {/* ---------- Retention offer modal ---------- */}
      {showRetention && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="retention-modal">
          <div className="w-full max-w-sm bg-[#141414] border border-gold/40 rounded-2xl p-5 text-center">
            <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center bg-gold/15 border border-gold/40 mb-3">
              <CreditCard size={24} weight="fill" color="#D4AF37" />
            </div>
            <p className="text-lg font-black gold-text">{t("account.retention_title")}</p>
            <p className="text-[13px] text-white/60 mt-2 mb-5">{t("account.retention_desc")}</p>
            <button onClick={acceptRetention} disabled={subBusy} data-testid="retention-accept"
              className="w-full h-11 rounded-xl bg-gold text-black font-bold text-sm hover:bg-gold/90 transition-colors disabled:opacity-60 mb-2">
              {subBusy ? t("account.saving") : t("account.retention_accept")}
            </button>
            <button onClick={doCancel} disabled={subBusy} data-testid="retention-decline"
              className="w-full h-11 rounded-xl bg-transparent text-white/50 font-semibold text-sm hover:text-white/80 transition-colors">
              {t("account.retention_decline")}
            </button>
          </div>
        </div>
      )}

      {/* ---------- Crop / zoom modal ---------- */}
      {cropSrc && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="avatar-crop-modal">
          <div className="w-full max-w-sm bg-[#141414] border border-white/10 rounded-2xl p-5">
            <p className="text-base font-bold mb-1">{t("account.crop_title")}</p>
            <p className="text-[12px] text-white/40 mb-4">{t("account.crop_hint")}</p>
            <div className="relative w-full h-64 rounded-xl overflow-hidden bg-black mb-4">
              <Cropper
                image={cropSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, pixels) => setCroppedPixels(pixels)}
              />
            </div>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-white/40">{t("account.zoom")}</span>
              <input type="range" min={1} max={3} step={0.05} value={zoom}
                data-testid="avatar-crop-zoom"
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1 accent-gold" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setCropSrc(null)} data-testid="avatar-crop-cancel"
                className="flex-1 h-11 rounded-xl bg-white/5 border border-white/10 text-white/70 font-bold text-sm hover:bg-white/10 transition-colors">
                {t("account.cancel")}
              </button>
              <button onClick={confirmCrop} disabled={uploadingPhoto} data-testid="avatar-crop-confirm"
                className="flex-1 h-11 rounded-xl bg-gold text-black font-bold text-sm hover:bg-gold/90 transition-colors disabled:opacity-60">
                {uploadingPhoto ? t("account.uploading") : t("account.crop_confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Delete confirmation modal ---------- */}
      {showDelete && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="account-delete-modal">
          <div className="w-full max-w-sm bg-[#141414] border border-red-500/30 rounded-2xl p-5 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-red-500/15 border border-red-500/40">
                <Trash size={18} weight="bold" color="#f87171" />
              </div>
              <p className="text-base font-bold">{t("account.delete_confirm_title")}</p>
            </div>
            <p className="text-[13px] text-white/50 mb-4">{t("account.delete_confirm_desc")}</p>
            <AccountDeletion onDone={async (result) => {
              navigate("/supprimer-compte", { state: { deletion: result } });
              await logout();
            }} />
            <button className="underline mt-3" onClick={() => setShowDelete(false)}>Retour</button>
          </div>
        </div>
      )}
    </>
  );
}
